import { isNativeApp } from '@/lib/platform';

/**
 * 原生系統分頁列（UITabBar，iOS 26+ 自動是 Liquid Glass；舊系統是標準半透明模糊）。
 * 只在 iOS App 裡存在；網站與不支援的環境 `glassTabBar()` 回 null，呼叫端退回 HTML 分頁列。
 *
 * 與 `mobile/ios-shell/native/App/GlassTabBarPlugin.swift` 的契約：
 *   configure({items:[{id,title,symbol}], selectedId}) → {height}（含底部 safe area，單位 pt）
 *   setSelected({id}) / hide()；事件 `tabSelected` → {id}
 */

export interface GlassTabItem {
  id: string;
  title: string;
  /** SF Symbol 名稱。 */
  symbol: string;
}

interface ListenerHandle {
  remove: () => Promise<void> | void;
}

interface GlassTabBarPlugin {
  configure(options: { items: GlassTabItem[]; selectedId: string }): Promise<{ height: number }>;
  setSelected(options: { id: string }): Promise<void>;
  hide(): Promise<void>;
  addListener(
    event: 'tabSelected',
    handler: (payload: { id: string }) => void,
  ): Promise<ListenerHandle> | ListenerHandle;
  /** 轉向／iPad 視窗改變後的新高度（含 safe area）。 */
  addListener(
    event: 'heightChanged',
    handler: (payload: { height: number }) => void,
  ): Promise<ListenerHandle> | ListenerHandle;
}

interface CapacitorPlugins {
  Capacitor?: { Plugins?: { GlassTabBar?: GlassTabBarPlugin } };
}

export function glassTabBar(): GlassTabBarPlugin | null {
  if (!isNativeApp()) return null;
  return (window as unknown as CapacitorPlugins).Capacitor?.Plugins?.GlassTabBar ?? null;
}

/** 原生分頁列的高度（含 safe area）換成 `--tabbar-h`（不含 safe area，與 CSS 慣例一致）。 */
export function publishTabbarHeight(totalHeight: number | null): void {
  const root = document.documentElement;
  if (totalHeight === null) {
    root.style.removeProperty('--tabbar-h');
    return;
  }
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;padding-bottom:env(safe-area-inset-bottom,0px)';
  document.body.appendChild(probe);
  const safeBottom = Number.parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  probe.remove();
  root.style.setProperty('--tabbar-h', `${String(Math.max(0, totalHeight - safeBottom))}px`);
}
